
import React, { useState } from 'react';
import { UseFormReturn } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ExternalLink, Download, Loader2, AlertCircle, Check, Folder } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TerracePhotoManagerProps {
  form: UseFormReturn<any>;
  currentPhotoUrl?: string;
  terraceId?: string;
  terraceName?: string;
}

const TerracePhotoManager: React.FC<TerracePhotoManagerProps> = ({ 
  form, 
  currentPhotoUrl,
  terraceId,
  terraceName
}) => {
  const [mapsPhotoUrl, setMapsPhotoUrl] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedFolderName, setSavedFolderName] = useState<string>('');
  
  const photoUrl = form.watch('photo_url') || currentPhotoUrl;

  // Function to sanitize terrace name for display (matches backend logic)
  const sanitizeForDisplay = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[åä]/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50)
      || 'unnamed-terrace';
  };

  const expectedFolderName = terraceName ? sanitizeForDisplay(terraceName) : 'unknown';

  const handleExtractMapsPhoto = async () => {
    if (!mapsPhotoUrl.trim()) {
      toast.error('Please enter a Google Maps photo URL');
      return;
    }

    setIsExtracting(true);
    try {
      console.log('Extracting photo URL from:', mapsPhotoUrl);
      
      const { data, error } = await supabase.functions.invoke('extract-maps-photo-url', {
        body: {
          mapsPhotoUrl: mapsPhotoUrl.trim(),
          maxWidth: 800,
          maxHeight: 600
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error('Failed to extract photo URL');
      }

      if (data?.success && data?.directUrl) {
        form.setValue('photo_url', data.directUrl);
        toast.success('Photo URL extracted successfully!');
        setMapsPhotoUrl(''); // Clear the input after success
      } else {
        throw new Error(data?.error || 'Could not extract photo URL from the provided Google Maps URL');
      }
    } catch (error) {
      console.error('Error extracting photo URL:', error);
      
      let errorMessage = 'Failed to extract photo URL';
      if (error.message?.includes('place ID')) {
        errorMessage = 'Could not find a valid place ID in the Google Maps URL. Please make sure you\'re using a direct link to a place with photos.';
      } else if (error.message?.includes('No photos')) {
        errorMessage = 'No photos found for this place. The location might not have any photos uploaded to Google Maps.';
      } else if (error.message?.includes('API key')) {
        errorMessage = 'Google Places API key is not configured. Please contact the administrator.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSavePhotoToStorage = async () => {
    if (!photoUrl || !terraceId) {
      toast.error('Photo URL and terrace ID are required');
      return;
    }

    setIsSaving(true);
    try {
      console.log('Saving photo to storage bucket for terrace:', terraceId);

      // Call the Edge Function to save the photo
      const { data, error } = await supabase.functions.invoke('admin-save-terrace-photo', {
        body: {
          photoUrl: photoUrl,
          terraceId: terraceId
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to save photo to storage');
      }

      if (data?.success) {
        // Update the form with the new storage URL
        form.setValue('photo_url', data.photoUrl);
        setSavedFolderName(data.folderName || '');
        toast.success(data.message || 'Photo saved to storage bucket successfully!');
        console.log('Photo saved to storage:', data.photoUrl);
        console.log('Folder name:', data.folderName);
      } else {
        throw new Error(data?.error || 'Failed to save photo to storage');
      }
      
    } catch (error) {
      console.error('Error saving photo to storage:', error);
      
      let errorMessage = 'Failed to save photo to storage';
      if (error.message?.includes('Admin access required')) {
        errorMessage = 'You need admin permissions to save photos to storage.';
      } else if (error.message?.includes('Authentication')) {
        errorMessage = 'Please log in to save photos to storage.';
      } else if (error.message?.includes('fetch photo')) {
        errorMessage = 'Could not download the photo from the provided URL. Please check if the URL is valid and accessible.';
      } else if (error.message?.includes('Terrace not found')) {
        errorMessage = 'The terrace could not be found. Please make sure the terrace exists.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Storage Folder Information */}
      {terraceName && (
        <Card className="card-warm border-peach-orange/20">
          <CardContent className="p-4">
            <div className="text-sm font-medium text-sunset-dark mb-2 flex items-center gap-2">
              <Folder className="h-4 w-4 text-peach-orange" />
              Storage Folder Structure
            </div>
            <div className="text-xs text-muted-foreground mb-2">
              Photos will be saved in a folder based on the terrace name:
            </div>
            <div className="bg-warm-cream/50 rounded px-3 py-2 font-mono text-sm border">
              <span className="text-sunset-medium">terraces/</span>
              <span className="text-peach-orange font-medium">{expectedFolderName}</span>
              <span className="text-sunset-medium">/main-photo-[timestamp].jpg</span>
            </div>
            {savedFolderName && (
              <div className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <Check className="h-3 w-3" />
                Last saved to folder: <code className="bg-green-50 px-1 rounded">{savedFolderName}</code>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current Photo Display */}
      {photoUrl && (
        <Card className="card-warm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-sunset-dark">Current Photo</div>
              {terraceId && (
                <Button
                  type="button"
                  onClick={handleSavePhotoToStorage}
                  disabled={isSaving}
                  variant="outline"
                  size="sm"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  {isSaving ? 'Saving...' : 'Save to Storage'}
                </Button>
              )}
            </div>
            <div className="aspect-video bg-muted rounded overflow-hidden max-w-sm border border-border">
              <img 
                src={photoUrl} 
                alt="Terrace photo preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden w-full h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <div>Invalid image URL</div>
                </div>
              </div>
            </div>
            {isSaving && (
              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving photo to folder: <code className="bg-muted px-1 rounded">{expectedFolderName}</code>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Manual Photo URL Input */}
      <div className="space-y-2">
        <Label htmlFor="photo_url">Photo URL</Label>
        <Input
          id="photo_url"
          type="url"
          placeholder="https://example.com/photo.jpg"
          value={photoUrl || ''}
          onChange={(e) => form.setValue('photo_url', e.target.value)}
        />
      </div>

      {/* Extract from Google Maps URL */}
      <Card className="card-warm">
        <CardContent className="p-4">
          <div className="text-sm font-medium text-sunset-dark mb-3 flex items-center gap-2">
            <ExternalLink className="h-4 w-4" />
            Extract Photo from Google Maps URL
          </div>
          <div className="text-xs text-muted-foreground mb-3">
            Paste a Google Maps place URL to extract the photo using Google Places API
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="https://maps.google.com/maps/place/... (place URL)"
              value={mapsPhotoUrl}
              onChange={(e) => setMapsPhotoUrl(e.target.value)}
              className="flex-1"
            />
            <Button
              type="button"
              onClick={handleExtractMapsPhoto}
              disabled={isExtracting || !mapsPhotoUrl.trim()}
              variant="outline"
            >
              {isExtracting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Extract
                </>
              )}
            </Button>
          </div>
          {isExtracting && (
            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Extracting photo using Google Places API...
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
            <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <div>
              Use a direct Google Maps place URL (not just a photo URL). The system will find the place and extract the first available photo.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TerracePhotoManager;
